import { useMemo } from "react";

import ChipsFilter from "components/chipsFilter/chipsFilter";
import TextButton from "components/textButton/TextButtonLegacy";
import type { ConfigType, TableDataType } from "../types";

export const Filter = ({
  tableEntries,
  filter,
  filterType,
  setFilter,
  setTableData,
  tableConfig,
  mixpanel,
  id,
}: {
  tableEntries: TableDataType[];
  filter: string;
  filterType: string;
  setFilter: (arg0: string) => void;
  setTableData: (arg0: TableDataType[]) => void;
  tableConfig: ConfigType;
  mixpanel: any;
  id: string | null;
}) => {
  const filterByData = useMemo(() => {
    const uniqueEntries = new Set();

    const count = tableEntries.reduce(
      (obj: { [key: string]: number } | {}, next: TableDataType) => {
        let data = next?.[filterType];
        if (!data) return obj;

        // Custom filter behaviour
        if (filterType === "location") {
          if (!Array.isArray(data)) {
            data = "Undefined";
          } else {
            data = data[1] ? data[1] : "Undefined";
          }
        }

        uniqueEntries.add(data);

        // Tallying the incidences of the category appearing - if it is not in the object
        // initialise the propertry with a count of 1, if it does increase the tally by one
        if (!obj[String(data)]) {
          obj[String(data)] = 1;
        } else {
          obj[String(data)] = obj[String(data)] += 1;
        }
        return obj;
      },
      {}
    );
    const topFilterOptions = Object.entries(count)
      .sort((a, b) => {
        if (!a[1] || !b[1]) return 0;
        // Primary sort: by count (descending)
        if (a[1] !== b[1]) {
          return a[1] < b[1] ? 1 : -1;
        }
        // Secondary sort: by name (ascending) for stable sorting
        return a[0].localeCompare(b[0]);
      })
      .slice(0, tableConfig.numberFilterOptions)
      .map((el) => el[0]);

    // Show all filter options unless the number has been set in the config
    const filterOptions = tableConfig.numberFilterOptions
      ? topFilterOptions
      : Array.from(uniqueEntries).sort();

    return {
      count,
      list: ["All", ...filterOptions],
    };
  }, [filterType, tableEntries, tableConfig.numberFilterOptions]);
  const handleFilter = (selected: string | string[]) => {
    const categories = Array.isArray(selected) ? selected : [selected];

    if (id && mixpanel) {
      mixpanel.track(`${tableConfig.trackingName} Table Filtering`, {
        "Filter Type": filterType,
        "Filtered By": categories.join(", "),
        distinct_id: id,
      });
    }

    if (categories.length === 0 || categories.includes("All")) {
      setTableData([...tableEntries]);
      setFilter("All");
      return;
    }
    const filteredData = tableEntries.filter((el: TableDataType) => {
      if (filterType === "location") {
        const country = el[filterType]?.[1];
        if (categories.includes("Undefined")) {
          return country === "" || categories.includes(country);
        }
        return categories.includes(country);
      }

      const value = el[filterType];
      return value && categories.includes(String(value));
    });

    setTableData([...filteredData]);
    setFilter(categories.join(", "));
  };

  const handleReset = () => {
    setTableData([...tableEntries]);
    setFilter("All");
  };
  if (filterByData.list.length <= 2) return null;

  const chipsList = filterByData.list
    .filter((category: string) => category !== "All")
    .map((category: string) => ({
      label: `${category} (${filterByData.count[category]})`,
      value: category,
    }));

  return (
    <div className="mt-[18px] md:mt-[22px]">
      <div className="mb-[12px] flex items-center justify-between">
        <h4 className="inline-flex font-bold text-[16px]">Region</h4>
        <div className="flex">
          <TextButton colour="black" component="button" onClick={handleReset}>
            All Regions
          </TextButton>
        </div>
      </div>
      {chipsList.length > 0 && (
        <ChipsFilter
          type={"multiple"}
          brandingColor="grey"
          selected={filter === "All" ? [] : filter.split(", ")}
          list={chipsList}
          onChange={handleFilter}
        />
      )}
    </div>
  );
};
